"use client";

import { useGlobal } from "@/app/providers/use-global";
import { Button } from "@/components/ui/button";
import Icon from "@/components/ui/icon";
import { Tooltip } from "@/components/ui/tooltip";
import { useScreen } from "@/hooks/use-screen";
import {
  type PromptAndResponse,
  useSendMessage,
} from "@/hooks/use-send-message";
import { INTERCOM_ACTION_COLOR, INTERCOM_BACKGROUD_COLOR } from "@/lib/consts";
import { cn } from "@/lib/utils";
import { copyToClipboard, isMobileDevice, isRtlText } from "@/lib/utils/index";
import { useUser } from "@/shared/providers/user-provider";
import { ChevronLast, CircleStopIcon, PlayCircle } from "lucide-react";
import { useSearchParams } from "next/navigation";
import {
  type ChangeEvent,
  type Dispatch,
  type FormEvent,
  type KeyboardEvent,
  type RefObject,
  type SetStateAction,
  createRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  memo,
  useMemo,
} from "react";
import { useIntercom } from "react-use-intercom";
import { toast } from "sonner";
import LoginCard from "../login-card";
import GroqIsFastSection from "../sections/groq-is-fast-section";
import ExamplePrompts from "./example-prompts";
import PromptDropdown from "./prompt-dropdown";
import ResponseBlock from "./response-block";
import ScrollToBottomButton from "./scroll-to-bottom-button";
import StatsBox, { type StatsBoxProps } from "./stats-box";
import InputForm, { type InputFormProps } from "./input-form";
import { ModelTermsWarning } from "./model-terms-warning";
import { ModelTermAcceptanceDialog } from "../dialogs/model-term-acceptance";
import SpeechSelect from "./speech-select";
import { TTSAudioBuffer } from "@/lib/utils/tts-audio-buffer";
import { ls } from "@/lib/utils/ls";

const LAUNCH_MODELS = [
  "mock-llama3-model",
  "llama-3.1-8b-instant",
  "llama-3.1-70b-versatile",
  "llama-3.1-405b-reasoning",
];

const modelTTSSourceMap = {
  english: "playai-tts",
  arabic: "playai-tts-arabic",
  mute: "playai-tts", // Default to English TTS model when muted
};

export interface speechStatus {
  isRecordingAudio: boolean;
  setIsRecordingAudio: Dispatch<SetStateAction<boolean>>;
  isFetching: boolean;
  setIsFetching: Dispatch<SetStateAction<boolean>>;
}

type InputStatsBoxProps = StatsBoxProps & InputFormProps;

const InputStatsBox = memo((props: InputStatsBoxProps) => {
  const {
    chat,
    handleClearChat,
    handleInput,
    handleSubmit,
    inputBuffer,
    isInputDisabled,
    setInputBuffer,
    textDirection,
    promptInputRef,
    isResponseExtended,
    setIsResponseExtended,
    inlineLoginScrollRef,
    isTTSLoading,
    isTTSPlaying,
    stopTTS,
  } = props;
  return (
    <div
      className={cn(
        "flex flex-col md:flex-col md:relative w-full max-w-[900px] bg-background z-10000 gap-2",
      )}
    >
      <div className="order-2 md:order-1 md:relative flex items-center">
        <InputForm
          {...{
            handleInput,
            handleSubmit,
            inputBuffer,
            isInputDisabled,
            setInputBuffer,
            textDirection,
            promptInputRef,
            inlineLoginScrollRef,
            chat,
            isTTSLoading,
            isTTSPlaying,
            stopTTS,
          }}
        />
        <SpeechSelect triggerClassName="md:absolute md:-left-11 md:top-[18px] mb-3 mr-3 md:m-0" />
      </div>
      <div className="order-1 md:order-2 justify-self-end md:hidden">
        <StatsBox
          {...{
            chat,
            handleClearChat,
            isResponseExtended,
            setIsResponseExtended,
          }}
        />
      </div>
    </div>
  );
});
InputStatsBox.displayName = "InputStatsBox";

interface LimitedTextProps {
  textDirection: "ltr" | "rtl";
  user_prompt: string;
}
const LimitedText = memo(({ textDirection, user_prompt }: LimitedTextProps) => {
  const [showFullText, setShowFullText] = useState<boolean>(false);
  const [isClamped, setIsClamped] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (textRef.current) {
      const hasOverflow =
        textRef.current.scrollHeight > textRef.current.clientHeight;
      setIsClamped(hasOverflow);
    }
  }, [user_prompt]);

  return (
    <div>
      <div
        ref={textRef}
        className={cn(
          "line-clamp-5",
          showFullText && "line-clamp-none",
          textDirection === "rtl" && "text-right",
        )}
      >
        {user_prompt}
      </div>
      {!showFullText && isClamped && (
        <span
          onClick={() => setShowFullText(true)}
          onKeyDown={() => setShowFullText(true)}
          className="text-primaryaccent cursor-pointer"
        >
          Show more
        </span>
      )}
      {showFullText && (
        <span
          onClick={() => setShowFullText(false)}
          onKeyDown={() => setShowFullText(false)}
          className="text-primaryaccent cursor-pointer"
        >
          Show less
        </span>
      )}
    </div>
  );
});
LimitedText.displayName = "LimitedText";

type ChatProps = {
  examplePrompts: string[];
};

export default function Chat({ examplePrompts }: ChatProps) {
  const {
    currentModel,
    initialUserPrompt,
    setSelectedPrompt,
    showLoginCardInHome,
    isResponseExtended,
    setIsResponseExtended,
    isIframe,
    isUserLoggedIn,
    mutateModels,
    ttsSource,
    selectedEnglishTtsVoice,
    selectedArabicTtsVoice,
    isEnglishTTSTermsAccepted,
    isArabicTTSTermsAccepted,
    modelsData,
    speechStatus,
  } = useGlobal();
  const { sendMessage, isProcessing } = useSendMessage();
  const [chat, setChat] = useState<PromptAndResponse[]>([]);
  const scrollChatListBottom = useRef<HTMLDivElement>(null);
  const inlineLoginScrollRef = useRef<HTMLDivElement>(null);
  const mainContentScrollRef = useRef<HTMLDivElement>(null);
  const chatRefs = useRef<RefObject<HTMLDivElement>[]>([]);
  const [isReceivingResponse, setIsReceivingResponse] =
    useState<boolean>(false);
  const [textDirection, setTextDirection] = useState<"ltr" | "rtl">("ltr");
  const promptInputRef = useRef<HTMLTextAreaElement>(null);
  const { profile, isStytchInitialized, buildRequestHeaders, currentOrg } =
    useUser();
  const searchParams = useSearchParams();
  const partner = searchParams.get("partner");
  const [inputBuffer, setInputBuffer] = useState<string>("");
  const supportedPartners = ["ms", "groq-test"];
  const { boot } = useIntercom();
  const { width: screenWidth, isMobile } = useScreen();
  const [isInputDisabled, setIsInputDisabled] = useState<boolean>(true);
  const [isTermsDialogOpen, setIsTermsDialogOpen] = useState<boolean>(false);
  const [isTTSTermsDialogOpen, setIsTTSTermsDialogOpen] =
    useState<boolean>(false);
  const ttsAudioBuffer = useRef<TTSAudioBuffer | null>(null);
  const [isTTSLoading, setIsTTSLoading] = useState<boolean>(false);
  const [isTTSPlaying, setIsTTSPlaying] = useState<boolean>(false);
  const ttsRequestControllerRef = useRef<AbortController | null>(null);
  const [, setIsTTSButtonDisabled] = useState<boolean>(false);
  const [isTTSIndex, setIsTTSIndex] = useState<number>(0);
  const [hasUserInitiatedPrompt, setHasUserInitiatedPrompt] =
    useState<boolean>(false);
  const [hasUserInitiatedTTS, setHasUserInitiatedTTS] =
    useState<boolean>(false);
  const lastResponseRef = useRef<string | undefined>();
  const voiceTTSSourceMap = {
    english: selectedEnglishTtsVoice,
    arabic: selectedArabicTtsVoice,
    mute: selectedEnglishTtsVoice, // Default to English voice when muted
  };

  const showGroqIsFastSection = useMemo(() => {
    return !(
      chat.length > 0 &&
      (chat[0].assistant_response.length > 0 ||
        chat[0].reasoning ||
        // biome-ignore lint/style/useExplicitLengthCheck: <explanation>
        chat[0].tool_calls?.length)
    );
  }, [chat]);

  const englishTTSModel = modelsData?.find((m) => m.id === "playai-tts");
  const arabicTTSModel = modelsData?.find((m) => m.id === "playai-tts-arabic");

  // Initialize TTSAudioBuffer in a proper useEffect
  useEffect(() => {
    if (typeof window !== "undefined") {
      ttsAudioBuffer.current = new TTSAudioBuffer({
        showToast: (message: string) => {
          if (!ttsAudioBuffer.current?.hasTTSSlowConnectionToastShown) {
            toast.warning(message);
            ttsAudioBuffer.current?.setHasTTSSlowConnectionToastShown(true);
          }
        },
        onAudioEnded: () => {
          setIsTTSPlaying(false);
          ttsAudioBuffer.current?.setHasTTSSlowConnectionToastShown(false);
        },
      });
    }
    // Use direct function reference in cleanup to avoid circular dependency
    return () => {
      if (ttsAudioBuffer.current) {
        ttsAudioBuffer.current.reset(false);
      }
      if (ttsRequestControllerRef.current) {
        ttsRequestControllerRef.current.abort();
      }
      setIsTTSButtonDisabled(false);
      setIsTTSPlaying(false);
      setIsTTSLoading(false);
    };
  }, []); // Empty dependency array since this should only run once

  // Define stopTTS function after state and before other functions
  const stopTTS = useCallback(() => {
    ttsAudioBuffer.current?.reset(false);
    ttsRequestControllerRef.current?.abort();
    ttsAudioBuffer.current?.setHasAudioEnded(true);
    ttsAudioBuffer.current?.setHasTTSSlowConnectionToastShown(false);
    setIsTTSButtonDisabled(false);
    setIsTTSPlaying(false);
    setIsTTSLoading(false);
  }, []);

  const handleInput = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      setInputBuffer(e.target.value);
      // Detect if text is RTL
      if (e.target.value.trim().length > 0) {
        setTextDirection(isRtlText(e.target.value) ? "rtl" : "ltr");
      } else {
        setTextDirection("ltr");
      }
    },
    [setInputBuffer, setTextDirection],
  );

  const focusInput = useCallback(() => {
    if (isMobileDevice() || !promptInputRef.current) {
      return;
    }
    promptInputRef.current.focus();
  }, []);

  const handleCopyPermalink = useCallback(
    (prompt: string, model: string | undefined) => {
      const url = new URL(window.location.href);
      const params = url.searchParams;
      if (!params.get("prompt")) {
        params.set("prompt", prompt);
      }
      if (!params.get("model") && model) {
        params.set("model", model);
      }
      copyToClipboard(url.toString(), "Link copied to clipboard");
      focusInput();
    },
    [focusInput],
  );

  const handleClearChat = useCallback(() => {
    setChat([]);
    stopTTS();
    setIsResponseExtended(false);
    focusInput();
  }, [stopTTS, setIsResponseExtended, focusInput]);

  const handleDequeueRequest = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      stopTTS();
      setInputBuffer("");
      setSelectedPrompt(undefined);
    },
    [stopTTS, setSelectedPrompt],
  );

  const handleSubmit = useCallback(
    async (
      e?: FormEvent<HTMLFormElement> | KeyboardEvent<HTMLTextAreaElement>,
      injected = "",
    ) => {
      e?.preventDefault();
      const prompt = injected || inputBuffer;
      if (!prompt.trim()) {
        // biome-ignore lint/suspicious/noConsole: <explanation>
        console.warn("No prompt provided");
        return;
      }
      if (
        currentModel?.is_terms_required &&
        !currentModel?.is_terms_accepted &&
        currentModel?.terms_url
      ) {
        setIsTermsDialogOpen(true);
        setHasUserInitiatedPrompt(true);
        // biome-ignore lint/suspicious/noConsole: <explanation>
        console.warn("Terms required");
        return;
      }

      const responseStartTime = new Date();

      setChat((previousChat) => [
        ...previousChat,
        {
          user_prompt: prompt,
          assistant_response: "",
          reasoning: "",
          tool_calls: [],
          edited_response: "",
          edited_stats: null,
          response_duration: null,
          edited_response_duration: null,
          stats: null,
          model: currentModel,
        },
      ]);

      setInputBuffer("");

      try {
        const result = await sendMessage({
          chatHistory: chat,
          setChatHistory: setChat,
          user_prompt: prompt ?? "",
          isEdit: false,
          setInputBuffer,
          scrollChatListBottom,
          setIsLoadingMessage: setIsReceivingResponse,
        });

        if (!result?.error) {
          const duration = (
            (Date.now() - responseStartTime.getTime()) /
            1000
          ).toFixed(2);
          setChat((previousChat) => {
            const lastItemIndex = previousChat.length - 1;
            if (
              lastItemIndex >= 0 &&
              !previousChat[lastItemIndex].response_duration
            ) {
              const updatedLastItem = {
                ...previousChat[lastItemIndex],
                response_duration: duration,
              };
              return [...previousChat.slice(0, lastItemIndex), updatedLastItem];
            }
            return previousChat;
          });
        }
      } catch {
        // This catch block might be redundant if sendMessage handles its own errors
        // console.error("Error in handleSubmit:", error);
        // toast.error("Failed to send message."); // Generic fallback
        // setChat((prevChat) => prevChat.slice(0, -1)); // Revert optimistic update
        // setInputBuffer(prompt); // Restore input
        // biome-ignore lint/suspicious/noConsole: <explanation>
        console.warn("Error in handleSubmit");
      } finally {
        setSelectedPrompt(undefined);

        if (isMobile && !isUserLoggedIn) {
          requestAnimationFrame(() => {
            inlineLoginScrollRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "end",
            });
          });
        } else if (!isUserLoggedIn) {
          requestAnimationFrame(() => {
            inlineLoginScrollRef.current?.scrollIntoView({
              behavior: "instant",
              block: "end",
            });
          });
        }
        focusInput();
      }
    },
    [
      chat,
      sendMessage,
      setChat,
      inputBuffer,
      isMobile,
      currentModel,
      stopTTS,
      setSelectedPrompt,
      setInputBuffer,
      setIsReceivingResponse,
      isUserLoggedIn,
      inlineLoginScrollRef,
      focusInput,
    ],
  );

  // Move this useEffect after handleSubmit has been defined
  const [triggerSend, setTriggerSend] = useState<string | null>(null);
  useEffect(() => {
    if (triggerSend && currentModel && !isProcessing && !isReceivingResponse) {
      handleSubmit(undefined, triggerSend);
      setTriggerSend(null);
    }
  }, [
    triggerSend,
    currentModel,
    handleSubmit,
    isProcessing,
    isReceivingResponse,
  ]);

  useEffect(() => {
    if (isInputDisabled) {
      return;
    }
    const timer = setTimeout(() => {
      promptInputRef.current?.focus({ preventScroll: true });
    }, 50);
    return () => clearTimeout(timer);
  }, [isInputDisabled]);

  useEffect(() => {
    if (!isStytchInitialized) {
      setIsInputDisabled(true);
      return;
    }
    if (!isUserLoggedIn) {
      setIsInputDisabled(false);
      return;
    }
    if (!currentModel) {
      setIsInputDisabled(true);
      return;
    }
    if (isProcessing || isReceivingResponse || isTTSLoading || isTTSPlaying) {
      setIsInputDisabled(true);
    } else {
      setIsInputDisabled(false);
    }
  }, [
    isStytchInitialized,
    isProcessing,
    isReceivingResponse,
    currentModel,
    isUserLoggedIn,
    isTTSLoading,
    isTTSPlaying,
  ]);

  useEffect(() => {
    if (isIframe) {
      window.parent.postMessage("chat-app-ready", "*");
    }
  }, [isIframe]);

  useEffect(() => {
    if (isIframe === false) {
      const customAttributes = {
        orgs: profile?.user.orgs?.data
          .map((org) => ({
            id: org.id,
            name: org.name,
            billingPlan: org.billing_plan,
          }))
          .sort((a, b) => a.name.localeCompare(b.name, "en"))
          .map((org) => `"${org.id}:${org.name}:${org.billingPlan ?? "free"}"`)
          .join(",\n\n"),
        currentOrg: `${currentOrg}`,
      };
      boot({
        userId: profile?.user.id,
        email: profile?.user.email,
        userHash: profile?.user.intercom_hash,
        hideDefaultLauncher: true,
        customAttributes,
        actionColor: INTERCOM_ACTION_COLOR,
        backgroundColor: INTERCOM_BACKGROUD_COLOR,
      });
    }
  }, [profile, screenWidth, isIframe, boot]);

  useEffect(() => {
    if (partner) {
      if (supportedPartners.includes(partner)) {
        localStorage.setItem("groq-partner", partner);
      } else {
        setTimeout(() => {
          toast.error("Partner code not found.", {
            id: "partner-not-found",
            position: "top-center",
          });
        }, 0);
      }
    }
  }, [partner, supportedPartners]);

  useEffect(() => {
    if (
      initialUserPrompt &&
      currentModel &&
      chat.length === 0 &&
      !isProcessing &&
      !isReceivingResponse
    ) {
      setInputBuffer(initialUserPrompt);
      handleSubmit(undefined, initialUserPrompt);
    }
  }, [initialUserPrompt, currentModel]);

  // TTS
  const fetchAudio = async ({
    model,
    voice,
    input,
  }: { model?: string; voice: string; input: string }) => {
    setIsTTSButtonDisabled(true);
    // this must be called after user interaction with the app
    await ttsAudioBuffer.current?.connectAudioContext();

    // Set a timeout to show a toast if the first byte takes too long
    const firstByteTimeout = setTimeout(() => {
      if (!ttsAudioBuffer.current?.hasTTSSlowConnectionToastShown) {
        toast.warning("Poor network connection. Expect audio degradation.");
        ttsAudioBuffer.current?.setHasTTSSlowConnectionToastShown(true);
      }
    }, 1000); // 1 second threshold
    try {
      const controller = new AbortController();
      ttsRequestControllerRef.current = controller;
      const headers = await buildRequestHeaders();
      const res = await fetch("https://api.groq.com/openai/v1/audio/speech", {
        method: "POST",
        headers,
        body: JSON.stringify({
          model,
          voice,
          input,
          response_format: "wav",
          speed: 1.0,
        }),
        signal: controller.signal,
      });

      if (res.ok && res.body) {
        setIsTTSButtonDisabled(false);
        const reader = res.body.getReader();
        setIsTTSPlaying(true);

        const pump = async () => {
          if (ttsRequestControllerRef.current?.signal.aborted) {
            clearTimeout(firstByteTimeout); // Clear the timeout if aborted
            return;
          }
          try {
            const { done, value } = await reader.read();
            clearTimeout(firstByteTimeout); // Clear the timeout on read

            if (done) {
              setIsTTSLoading(false);
              ttsAudioBuffer.current?.setHasAudioEnded(true);
              return;
            }
            ttsAudioBuffer.current?.addChunk(value);
          } catch (_) {
            //console.error("Error adding chunk", error);
            clearTimeout(firstByteTimeout); // Clear the timeout on error
          }

          pump();
        };
        pump();
      } else {
        clearTimeout(firstByteTimeout); // Clear the timeout if the response is not successful
        const errorMessage = await res.text();
        try {
          const error = JSON.parse(errorMessage);
          toast.error(`${error.error?.message || error.error}`);
        } catch (_e) {
          toast.error(`${errorMessage}`);
        }
        setIsTTSButtonDisabled(false);
        setIsTTSLoading(false);
        setIsTTSPlaying(false);
      }
    } catch (_) {
      clearTimeout(firstByteTimeout); // Clear the timeout on error
      //console.error("Error generating audio", error);
      setIsTTSButtonDisabled(false);
      setIsTTSLoading(false);
    }
  };

  // Handle TTS for the last response if tts is enabled
  useEffect(() => {
    const lastChatEntry = chat.at(-1);
    const lastResponse =
      lastChatEntry?.edited_response || lastChatEntry?.assistant_response;
    const canTriggerTTS =
      !(isProcessing || isReceivingResponse) && lastResponse && chat.length > 0;

    const responseChanged = lastResponse !== lastResponseRef.current;
    lastResponseRef.current = lastResponse;

    if (canTriggerTTS && responseChanged) {
      setIsTTSIndex(chat.length - 1);
      if (ttsSource === "mute") {
        return;
      }

      let termsAccepted = true;
      let dialogToOpen = false;
      if (ttsSource === "english" && !isEnglishTTSTermsAccepted) {
        termsAccepted = false;
        dialogToOpen = true;
      }
      if (ttsSource === "arabic" && !isArabicTTSTermsAccepted) {
        termsAccepted = false;
        dialogToOpen = true;
      }
      if (!termsAccepted) {
        setIsTTSTermsDialogOpen(dialogToOpen);
        setHasUserInitiatedTTS(true);
        return;
      }

      fetchAudio({
        model: modelTTSSourceMap[ttsSource],
        voice: voiceTTSSourceMap[ttsSource],
        input: lastResponse,
      }).catch((error) => {
        // biome-ignore lint/suspicious/noConsole: <explanation>
        console.error("Error auto-fetching TTS audio:", error);
      });
    }
  }, [
    chat,
    isProcessing,
    isReceivingResponse,
    ttsSource,
    isEnglishTTSTermsAccepted,
    isArabicTTSTermsAccepted,
    stopTTS,
    fetchAudio,
    voiceTTSSourceMap,
    modelTTSSourceMap,
    setIsTTSTermsDialogOpen,
    setIsTTSIndex,
  ]);

  const playTTS = useCallback(
    async (index: number) => {
      let currentTtsSource = ttsSource;
      if (ttsSource === "mute") {
        currentTtsSource = "english";
      }

      let termsAccepted = true;
      let dialogToOpen = false;
      if (currentTtsSource === "english" && !isEnglishTTSTermsAccepted) {
        termsAccepted = false;
        dialogToOpen = true;
      }
      if (currentTtsSource === "arabic" && !isArabicTTSTermsAccepted) {
        termsAccepted = false;
        dialogToOpen = true;
      }
      if (!termsAccepted) {
        setIsTTSTermsDialogOpen(dialogToOpen);
        setHasUserInitiatedTTS(true);
        return;
      }

      setIsTTSIndex(index);
      setIsTTSButtonDisabled(true);

      const lastResponse =
        chat[index]?.edited_response || chat[index]?.assistant_response;

      if (lastResponse) {
        let truncatedInput = lastResponse;
        if (ttsSource === "mute") {
          if (lastResponse.length > 500) {
            // Take the first 500 characters
            truncatedInput = lastResponse.slice(0, 500);
            // Find the first space or period after the 500th character
            const nextSpaceOrPeriodIndex = Math.min(
              lastResponse.indexOf(" ", 500),
              lastResponse.indexOf(".", 500),
              lastResponse.indexOf(",", 500),
            );
            if (nextSpaceOrPeriodIndex > 500) {
              truncatedInput = lastResponse.slice(0, nextSpaceOrPeriodIndex);
            }
          }
        }
        await fetchAudio({
          model: modelTTSSourceMap[currentTtsSource],
          voice: voiceTTSSourceMap[currentTtsSource],
          input: truncatedInput,
        });
      } else {
        setIsTTSButtonDisabled(false);
        toast.info("No text to play.");
      }
    },
    [
      chat,
      ttsSource,
      isEnglishTTSTermsAccepted,
      isArabicTTSTermsAccepted,
      stopTTS,
      fetchAudio,
      voiceTTSSourceMap,
      modelTTSSourceMap,
      setIsTTSTermsDialogOpen,
      setIsTTSButtonDisabled,
      setIsTTSIndex,
    ],
  );

  const handleToggleToolVisibility = useCallback(
    (chatIndex: number, toolCallIndexValue: number) => {
      setChat((prevChat) => {
        const newChat = [...prevChat];
        const chatItem = newChat[chatIndex];

        if (chatItem?.tool_calls) {
          const toolCallIndexInArray = chatItem.tool_calls.findIndex(
            (tc) => tc.index === toolCallIndexValue,
          );

          if (toolCallIndexInArray !== -1) {
            const updatedToolCalls = [...chatItem.tool_calls];
            updatedToolCalls[toolCallIndexInArray] = {
              ...updatedToolCalls[toolCallIndexInArray],
              isOutputVisible:
                !updatedToolCalls[toolCallIndexInArray].isOutputVisible,
            };

            newChat[chatIndex] = {
              ...chatItem,
              tool_calls: updatedToolCalls,
            };

            return newChat;
          }
        }
        return prevChat;
      });
    },
    [setChat],
  );

  // If the user has initiated a prompt and the model terms are accepted, submit the prompt
  useEffect(() => {
    if (
      currentModel?.is_terms_required &&
      currentModel?.is_terms_accepted &&
      currentModel?.terms_url &&
      hasUserInitiatedPrompt
    ) {
      handleSubmit();
      setHasUserInitiatedPrompt(false);
    }
  }, [currentModel]);

  // If the user has initiated TTS and the model terms are accepted, play the TTS
  useEffect(() => {
    if (
      ttsSource === "english" &&
      isEnglishTTSTermsAccepted &&
      englishTTSModel &&
      hasUserInitiatedTTS
    ) {
      playTTS(isTTSIndex);
      setHasUserInitiatedTTS(false);
    } else if (
      ttsSource === "arabic" &&
      isArabicTTSTermsAccepted &&
      arabicTTSModel &&
      hasUserInitiatedTTS
    ) {
      playTTS(isTTSIndex);
      setHasUserInitiatedTTS(false);
    }
  }, [
    isEnglishTTSTermsAccepted,
    isArabicTTSTermsAccepted,
    hasUserInitiatedTTS,
    isTTSIndex,
  ]);

  // auto submit the unauthenticatedUserPrompt after user logs in
  useEffect(() => {
    const userPrompt = ls.get("unauthenticatedUserPrompt");

    if (isUserLoggedIn && userPrompt && currentModel) {
      setInputBuffer(userPrompt.text);

      // added timeout so that all active tabs have
      // enough time to read from local storage
      setTimeout(() => {
        ls.remove("unauthenticatedUserPrompt");
      }, 1000);

      // if we want only active tab to auto submit the prompt
      // check if document.hasFocus()
      // currently, all active tabs will auto submit the prompt
      handleSubmit(undefined, userPrompt.text);
    }
  }, [isUserLoggedIn, currentModel]);

  return (
    <>
      <div className="grid grid-cols-6 justify-center items-start justify-items-center px-6 auto-rows-max">
        <div className="md:col-span-4 col-span-6 md:col-start-2 col-start-1 flex flex-col w-full max-w-[900px]">
          <h1
            className={cn(
              "pt-4 md:pt-0 pb-8 px-4 text-xl text-primaryaccent text-center md:hidden group-[.iframe]:hidden",
              chat.length > 0 && isMobileDevice() && "hidden",
            )}
          >
            Groq is Fast AI Inference
          </h1>
          <div className="hidden md:block w-full max-h-[calc(100%-60px)]">
            <InputStatsBox
              {...{
                chat,
                handleClearChat,
                handleInput,
                handleSubmit,
                inputBuffer,
                isInputDisabled: isInputDisabled || showLoginCardInHome,
                setInputBuffer,
                textDirection,
                promptInputRef,
                isResponseExtended,
                setIsResponseExtended,
                inlineLoginScrollRef,
                isTTSLoading,
                isTTSPlaying,
                stopTTS,
              }}
            />
          </div>
        </div>
      </div>
      <div className="grow overflow-auto grid justify-items-stretch md:grid-cols-6 md:justify-center md:items-start md:justify-items-center md:px-6 md:auto-rows-max">
        <div
          className={cn(
            "w-full h-full flex flex-col overflow-auto max-w-[900px] md:flex-col items-center z-10 md:col-span-4 md:col-start-2 md:justify-start",
            isResponseExtended && "md:col-start-1 md:col-span-6 max-w-screen",
          )}
        >
          <div className="hidden md:block w-full max-w-[900px]">
            <StatsBox
              {...{
                chat,
                handleClearChat,
                isResponseExtended,
                setIsResponseExtended,
              }}
            />
          </div>

          <div
            className="grow w-full overflow-auto flex flex-col px-0 md:px-0"
            ref={mainContentScrollRef}
          >
            {showGroqIsFastSection && (
              <GroqIsFastSection
                className={cn(
                  "block md:hidden mb-auto px-5",
                  chat.length > 0 && "order-1 mt-4",
                )}
              />
            )}

            <div className="hidden md:block px-5 pt-4">
              {currentModel?.is_terms_required &&
                !currentModel?.is_terms_accepted &&
                currentModel?.terms_url && (
                  <ModelTermsWarning
                    model={currentModel}
                    onAccept={() => {
                      mutateModels();
                    }}
                  />
                )}
              {ttsSource === "english" &&
              !isEnglishTTSTermsAccepted &&
              englishTTSModel ? (
                <ModelTermsWarning
                  model={englishTTSModel}
                  onAccept={() => {
                    mutateModels();
                  }}
                />
              ) : ttsSource === "arabic" &&
                !isArabicTTSTermsAccepted &&
                arabicTTSModel ? (
                <ModelTermsWarning
                  model={arabicTTSModel}
                  onAccept={() => {
                    mutateModels();
                  }}
                />
              ) : null}
            </div>

            {!(
              chat.length > 0 ||
              inputBuffer.includes("\n") ||
              showLoginCardInHome ||
              LAUNCH_MODELS.includes(currentModel?.id || "") ||
              speechStatus.isFetching ||
              isProcessing ||
              isReceivingResponse
            ) && (
              <div
                className={cn(
                  "flex flex-col mt-auto md:mt-0 px-5",
                  isResponseExtended && "md:grid md:grid-cols-6",
                )}
              >
                <ExamplePrompts
                  examplePrompts={examplePrompts}
                  isDisabled={isInputDisabled}
                  onExamplePromptSelected={(examplePrompt) => {
                    setInputBuffer(examplePrompt);
                    handleSubmit(undefined, examplePrompt);
                  }}
                />
              </div>
            )}

            {chat.length <= 0 &&
              LAUNCH_MODELS.includes(currentModel?.id || "") && (
                <div className="p-4 px-5">
                  Llama 3.1 models are now available. Read the blog post{" "}
                  <a
                    className="text-primaryaccent font-light underline"
                    href="https://groq.link/llama3405bblog"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    here.
                  </a>{" "}
                  These models are experiencing high demand. Daily token limits
                  are in effect. If you run out, please come back tomorrow 😁
                </div>
              )}

            {!isUserLoggedIn && showLoginCardInHome && (
              <div className="w-full flex-1 flex flex-col pt-3 items-center justify-center px-5">
                <h2 className="text-lg font-semibold leading-none tracking-tight text-center mb-2">
                  Login to use Groq
                </h2>
                <div className="max-w-[500px] bg-background p-6 w-full">
                  <LoginCard />
                </div>
              </div>
            )}

            {speechStatus.isFetching && (
              <div className="flex flex-col text-foreground mb-6 ml-5 px-5">
                <div className="flex items-start">
                  <Icon
                    icon="dots-loading"
                    className="w-14 fill-secondary-foreground"
                  />
                </div>
              </div>
            )}
            <div ref={inlineLoginScrollRef} />

            {chat
              .slice()
              .reverse()
              .map((conv, key) => {
                const originalIndex = chat.length - 1 - key;
                const ref =
                  chatRefs.current[originalIndex] ||
                  createRef<HTMLDivElement>();
                chatRefs.current[originalIndex] = ref;

                return (
                  <div
                    className={cn(
                      "flex flex-col [word-break:break-word]",
                      key !== 0 && "mb-6",
                    )}
                    key={`${originalIndex}-${conv.user_prompt.substring(0, 20)}`}
                  >
                    <div className="flex flex-row align-top mx-5 mb-4 md:mb-6 mt-4 md:mt-0 gap-2 group/input font-medium text-lg break-words whitespace-pre-wrap flex-1 relative">
                      <LimitedText
                        user_prompt={conv.user_prompt}
                        textDirection={textDirection}
                      />
                      <div ref={ref} className="scroll-mt-12 md:scroll-mt-8" />
                      <div className="flex flex-row items-center gap-1 absolute -bottom-6 right-0">
                        <Tooltip
                          trigger={
                            <Button
                              variant="ghost"
                              onClick={() => {
                                copyToClipboard(conv.user_prompt);
                                focusInput();
                              }}
                              className="hidden group-hover/input:flex group/copy bg-background hover:bg-background p-1 h-7 w-7 justify-center items-center"
                            >
                              <Icon
                                icon="copy"
                                className="group-hover/copy:fill-foreground fill-primaryaccent"
                                size="sm"
                              />
                            </Button>
                          }
                          providerProps={{ delayDuration: 200 }}
                          contentProps={{ side: "left" }}
                        >
                          Copy Content
                        </Tooltip>
                        <Tooltip
                          trigger={
                            <Button
                              variant="ghost"
                              onClick={() =>
                                handleCopyPermalink(
                                  conv.user_prompt,
                                  conv.model?.id,
                                )
                              }
                              className="hidden group-hover/input:flex group/copy bg-background hover:bg-background p-1 h-7 w-7 justify-center items-center"
                            >
                              <Icon
                                icon="externalLink"
                                className="group-hover/copy:stroke-foreground fill-transparent stroke-primaryaccent size-4"
                              />
                            </Button>
                          }
                          providerProps={{ delayDuration: 200 }}
                          contentProps={{ side: "left" }}
                        >
                          Copy Link
                        </Tooltip>
                      </div>
                    </div>

                    <div className="flex flex-col font-normal text-base px-5">
                      {key === 0 && isProcessing && !isReceivingResponse ? (
                        <div className="w-full flex flex-col text-foreground mb-4">
                          <div className="flex items-start">
                            <div className="flex items-center">
                              <p>You are in the queue.</p>
                              <Icon
                                icon="dots-loading"
                                className="w-14 fill-secondary-foreground"
                              />
                            </div>
                          </div>
                          <Button
                            variant={"outline"}
                            className="px-6 py-2 font-semibold w-fit mt-3"
                            onClick={handleDequeueRequest}
                          >
                            STOP
                          </Button>
                        </div>
                      ) : (
                        <ResponseBlock
                          response={
                            conv.edited_response || conv.assistant_response
                          }
                          reasoning={conv.reasoning}
                          tool_calls={conv.tool_calls}
                          onToggleToolVisibility={(toolIndexValue) =>
                            handleToggleToolVisibility(
                              originalIndex,
                              toolIndexValue,
                            )
                          }
                        />
                      )}

                      {(!isProcessing || key !== 0) &&
                        (conv.assistant_response || conv.edited_response) && (
                          <div className="flex flex-row align-center items-center gap-1 pt-2">
                            <Tooltip
                              trigger={
                                <Button
                                  className="w-min cursor-pointer h-auto p-2 hover:bg-transparent"
                                  variant={"ghost"}
                                  onClick={() => {
                                    copyToClipboard(
                                      conv.edited_response ||
                                        conv.assistant_response ||
                                        "",
                                    );
                                    focusInput();
                                  }}
                                >
                                  <Icon
                                    icon="copy"
                                    className="md:hover:fill-foreground fill-primaryaccent"
                                    size="sm"
                                  />
                                </Button>
                              }
                              providerProps={{ delayDuration: 200 }}
                              contentProps={{ side: "bottom" }}
                            >
                              Copy Content
                            </Tooltip>

                            <Tooltip
                              trigger={
                                <Button
                                  className="w-min cursor-pointer z-0 h-auto p-2 hover:bg-transparent"
                                  variant={"ghost"}
                                  onClick={() => {
                                    ref.current?.scrollIntoView({
                                      behavior: "smooth",
                                      block: isMobile ? "end" : "start",
                                    });
                                  }}
                                >
                                  <ChevronLast
                                    size={20}
                                    className="-rotate-90 md:hover:text-foreground text-primaryaccent"
                                  />
                                </Button>
                              }
                              providerProps={{ delayDuration: 200 }}
                              contentProps={{ side: "bottom" }}
                            >
                              Scroll To Top
                            </Tooltip>

                            <Tooltip
                              trigger={
                                <Button
                                  className={cn(
                                    "w-min cursor-pointer z-0 h-auto p-2 hover:bg-transparent disabled:opacity-50",
                                    isRtlText(conv.assistant_response) &&
                                      "hidden",
                                  )}
                                  variant={"ghost"}
                                  disabled={
                                    (isTTSLoading || isTTSPlaying) &&
                                    isTTSIndex !== originalIndex
                                  }
                                  onClick={() => {
                                    if (
                                      isTTSPlaying &&
                                      isTTSIndex === originalIndex
                                    ) {
                                      stopTTS();
                                    } else {
                                      playTTS(originalIndex);
                                    }
                                  }}
                                >
                                  {isTTSLoading &&
                                  isTTSIndex === originalIndex ? (
                                    <Icon
                                      icon="dots-loading"
                                      className="w-5 h-5 fill-primaryaccent"
                                    />
                                  ) : isTTSPlaying &&
                                    isTTSIndex === originalIndex ? (
                                    <CircleStopIcon
                                      size={20}
                                      className="md:hover:text-foreground text-primaryaccent"
                                    />
                                  ) : (
                                    <PlayCircle
                                      size={20}
                                      className="md:hover:text-foreground text-primaryaccent"
                                    />
                                  )}
                                </Button>
                              }
                              providerProps={{ delayDuration: 200 }}
                              contentProps={{ side: "bottom" }}
                              className="capitalize"
                            >
                              {isTTSLoading && isTTSIndex === originalIndex
                                ? "Loading Audio..."
                                : isTTSPlaying && isTTSIndex === originalIndex
                                  ? "Stop Playback"
                                  : "Play Audio"}
                            </Tooltip>

                            {key === 0 && isUserLoggedIn && (
                              <PromptDropdown
                                key={`edit-${originalIndex}-${conv.edited_response ? "edited" : "original"}`}
                                chat={chat}
                                setChat={setChat}
                                setIsLoadingMessage={setIsReceivingResponse}
                                setInputBuffer={setInputBuffer}
                                sendMessage={sendMessage}
                                scrollChatListBottom={scrollChatListBottom}
                                scrollChatListTop={ref}
                              />
                            )}
                          </div>
                        )}
                    </div>
                    {key === 0 && (
                      <div ref={scrollChatListBottom} className="h-1" />
                    )}
                  </div>
                );
              })}

            {showGroqIsFastSection && (
              <GroqIsFastSection className="hidden md:block px-5 mt-auto pt-6" />
            )}

            {chat.length > 0 && (!isProcessing || chat.length > 1) && (
              <ScrollToBottomButton containerRef={mainContentScrollRef} />
            )}
          </div>

          <div className="block md:hidden mt-4 px-5">
            {ttsSource === "english" &&
              !isEnglishTTSTermsAccepted &&
              englishTTSModel && (
                <ModelTermsWarning
                  model={englishTTSModel}
                  onAccept={mutateModels}
                />
              )}
            {ttsSource === "arabic" &&
              !isArabicTTSTermsAccepted &&
              arabicTTSModel && (
                <ModelTermsWarning
                  model={arabicTTSModel}
                  onAccept={mutateModels}
                />
              )}
            {currentModel?.is_terms_required &&
              !currentModel?.is_terms_accepted &&
              currentModel?.terms_url && (
                <ModelTermsWarning
                  model={currentModel}
                  onAccept={mutateModels}
                />
              )}
          </div>

          <div className="w-full md:hidden sticky bottom-0 bg-background pt-2 pb-4 border-t border-border">
            <InputStatsBox
              {...{
                chat,
                handleClearChat,
                handleInput,
                handleSubmit,
                inputBuffer,
                isInputDisabled: isInputDisabled || showLoginCardInHome,
                setInputBuffer,
                textDirection,
                isResponseExtended,
                setIsResponseExtended,
                inlineLoginScrollRef,
                isTTSLoading,
                isTTSPlaying,
                stopTTS,
              }}
            />
          </div>
        </div>
      </div>

      {isTermsDialogOpen && currentModel?.terms_url && (
        <ModelTermAcceptanceDialog
          id={currentModel.id}
          termsURL={currentModel.terms_url}
          onSuccess={() => {
            mutateModels();
            setIsTermsDialogOpen(false);
          }}
          onClose={() => setIsTermsDialogOpen(false)}
        />
      )}
      {isTTSTermsDialogOpen &&
        ((ttsSource === "english" || ttsSource === "mute") &&
        englishTTSModel?.terms_url ? (
          <ModelTermAcceptanceDialog
            id={englishTTSModel.id}
            termsURL={englishTTSModel.terms_url}
            onSuccess={() => {
              mutateModels();
              setIsTTSTermsDialogOpen(false);
            }}
            onClose={() => setIsTTSTermsDialogOpen(false)}
          />
        ) : ttsSource === "arabic" && arabicTTSModel?.terms_url ? (
          <ModelTermAcceptanceDialog
            id={arabicTTSModel.id}
            termsURL={arabicTTSModel.terms_url}
            onSuccess={() => {
              mutateModels();
              setIsTTSTermsDialogOpen(false);
            }}
            onClose={() => setIsTTSTermsDialogOpen(false)}
          />
        ) : null)}
    </>
  );
}
